import { useCallback, useEffect, useMemo, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Button, Collapse, IconButton } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { CompaniesByFirm, CompanyMeasurementDate, MeasurementDateRolloverReturn } from 'api';
import { FIX_BEFORE_CONTINUE, NEW_MEASUREMENT_DATE } from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { CompanyInfo } from 'common/types/company';
import { Fund } from 'common/types/fund';
import { UseStoreValues } from 'common/types/store';
import { MDContext } from 'context/MDContext';
import { CompanyIdMeasurementDateOptionsMap, CompanyItem, FundItem, MDContextType } from 'context/MDContext/types';
import { COMPLETE, FAILED } from 'hooks/useRollingApiRequest/useRollingApiRequest.props';
import {
  FUND_INVESTMENTS_COMPANIES_TITLE,
  FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE,
} from 'pages/FundInvestments/common/constants';
import { useRefreshFirmCompanies } from 'services/hooks/firms';
import { useGetCompaniesFromPreviousMD } from 'services/hooks/firms/useGetCompaniesFromPreviousMD/useGetCompaniesFromPreviousMD';
import { CompanyRollover } from 'services/hooks/useRolloverCompanies/rollover.types';
import useRolloverCompanies from 'services/hooks/useRolloverCompanies/useRolloverCompanies';
import { getObjectValue, MOMENT_DEFAULT_DATE_FORMAT, objToArray } from 'utilities';
import { FIRST, SECOND, THIRD } from './MeasurementDateDialog.constants';
import { MeasurementDateDialogProps } from './MeasurementDateDialog.props';
import { StyledActionButtonsGrid, StyledDialog } from './MeasurementDateDialog.styles';
import CompaniesTable from '../components/companies-table/CompaniesTable';
import CompaniesSelector from '../components/CompaniesSelector';
import RolloverStatus from '../components/RolloverStatus/components/rollover-status/RolloverStatus';
import { COMPANIES_LOAD_FAILED_MESSAGE, COMPANIES_LOADING_MESSAGE, NONE } from '../constants';
import { MeasurementDateLabel } from '../types';

const isCompanyInFundGroup = (company: CompaniesByFirm, fundId: FundItem['id']) =>
  fundId === null ? !company.fund_id : company.fund_id === fundId;

const toCompanyItem = (company: CompaniesByFirm): CompanyItem => ({
  id: company.company_id,
  name: company.company_name,
  status: company.status,
});

const MeasurementDateDialog = (props: MeasurementDateDialogProps) => {
  const { allowEscapeClose = false } = props;
  const { open, onClose, activeFund, activeCompany, getMeasurementDates, getFundMeasurementDates } = props;

  const [{ firmInfo, fundList }] = useStore() as UseStoreValues;
  const { are_fund_investments_enabled: areFundInvestmentsEnabled = false } = getObjectValue(firmInfo);

  const [measurementDate, setMeasurementDate] = useState<string | undefined>();
  const [selectedFunds, setSelectedFunds] = useState<{ [key: string]: boolean }>({});
  const [selectedCompanies, setSelectedCompanies] = useState<{ [key: number]: boolean }>({});
  const [rolloverDetailCompanies, setRolloverDetailCompanies] = useState<CompanyRollover[]>([]);
  const [activeStep, setActiveStep] = useState(FIRST);
  const [rollover, setRollover] = useState(false);
  const [shouldResetFinancials, setShouldResetFinancials] = useState(false);
  const [shouldExcludeDocuments, setShouldExcludeDocuments] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { companies, getCompanies, status: companiesStatus } = useGetCompaniesFromPreviousMD();
  const loadingCompanies = companiesStatus === 'loading';
  const companiesLoaded = companiesStatus === 'loaded';
  const { refreshFirmCompanies } = useRefreshFirmCompanies();

  const selectedFundNames = useMemo(
    () => fundList?.filter(fund => selectedFunds[fund.id as number]).map(fund => fund.name as string) ?? [],
    [fundList, selectedFunds]
  );

  // each company
  const sortedMeasurementDateOptionsByCompanyId = useMemo(
    () =>
      companies.reduce((acc, company) => {
        const options = company.measurement_dates
          .map(date => moment(date))
          .sort((a, b) => b.diff(a))
          .map(date => date.format(MOMENT_DEFAULT_DATE_FORMAT));
        options.unshift(NONE);
        acc[company.company_id] = options;
        return acc;
      }, {} as CompanyIdMeasurementDateOptionsMap),
    [companies]
  );

  const latestPossibleMarketDate = useMemo(() => moment().subtract(1, 'days'), []);

  const marketDate = useMemo(() => {
    const md = moment(measurementDate);
    return md.isAfter(latestPossibleMarketDate, 'day')
      ? latestPossibleMarketDate.format(MOMENT_DEFAULT_DATE_FORMAT)
      : md.format(MOMENT_DEFAULT_DATE_FORMAT);
  }, [measurementDate, latestPossibleMarketDate]);

  const companiesWithoutFundTitle = useMemo(
    () =>
      `${
        areFundInvestmentsEnabled ? FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE : FUND_INVESTMENTS_COMPANIES_TITLE
      } without Fund`,
    [areFundInvestmentsEnabled]
  );

  const activeCompanyItem = useMemo<CompanyItem | null>(() => {
    if (!activeCompany?.id) {
      return null;
    }

    return {
      id: activeCompany.id as number,
      name: activeCompany.name,
    };
  }, [activeCompany?.id, activeCompany?.name]);

  const shouldIncludeActiveCompany = useMemo(
    () => Boolean(activeCompanyItem && !companies.some(company => company.company_id === activeCompanyItem.id)),
    [activeCompanyItem, companies]
  );

  const items = useMemo<FundItem[]>(() => {
    const companiesWithoutFund: MeasurementDateLabel = { id: null, name: companiesWithoutFundTitle };
    const funds: (Fund | MeasurementDateLabel)[] = [companiesWithoutFund, ...((fundList as Fund[]) ?? [])];

    let nextItems = funds.map(fund => ({
      id: fund.id,
      name: fund.name as string,
      subItems: companies.filter(company => isCompanyInFundGroup(company, fund.id)).map(toCompanyItem),
    }));

    if (activeCompanyItem && shouldIncludeActiveCompany) {
      nextItems = nextItems.map((item, index) =>
        index === 0 ? { ...item, subItems: [activeCompanyItem, ...item.subItems] } : item
      );
    }

    return companies.length > 0 || shouldIncludeActiveCompany ? nextItems : [];
  }, [activeCompanyItem, companies, companiesWithoutFundTitle, fundList, shouldIncludeActiveCompany]);

  const subItems = useMemo(() => items.flatMap(item => item.subItems), [items]);

  const createRolloverDetail = useCallback(
    (companyId: number, companyName: string, customDate = NONE, isFundInvestment = false): CompanyRollover => ({
      companyId,
      companyName,
      customDate,
      excludeDocuments: false,
      excludeValuation: false,
      isFundInvestment,
      keepMarketData: false,
      marketDate: marketDate || null,
      resetFinancials: false,
    }),
    [marketDate]
  );

  const rolloverCompanyById = useMemo(() => {
    const entries = companies.map(company => {
      const sortedMeasurementDates = sortedMeasurementDateOptionsByCompanyId[company.company_id] ?? [NONE];
      // If there is only none in the array, use that, else use the most recent (second record).
      const mdDate = sortedMeasurementDates.length === 1 ? sortedMeasurementDates[0] : sortedMeasurementDates[1];

      return [
        company.company_id,
        createRolloverDetail(
          company.company_id,
          company.company_name,
          mdDate === NONE ? mdDate : moment(mdDate).format(MOMENT_DEFAULT_DATE_FORMAT),
          company.company_type === CompaniesByFirm.company_type.INVESTMENT
        ),
      ] as const;
    });

    if (activeCompanyItem && shouldIncludeActiveCompany) {
      // The selected CompanyInfo may exist before the previous-MD endpoint includes the new company.
      entries.push([activeCompanyItem.id, createRolloverDetail(activeCompanyItem.id, activeCompanyItem.name)] as const);
    }

    return Object.fromEntries(entries) as Record<number, CompanyRollover>;
  }, [
    activeCompanyItem,
    companies,
    createRolloverDetail,
    shouldIncludeActiveCompany,
    sortedMeasurementDateOptionsByCompanyId,
  ]);

  useEffect(() => {
    const rolloverDetails = Object.entries(selectedCompanies)
      .filter(([, isSelected]) => isSelected)
      .map(([companyId]) => rolloverCompanyById[Number(companyId)])
      .filter((company): company is CompanyRollover => Boolean(company));

    setRolloverDetailCompanies(rolloverDetails);
  }, [selectedCompanies, rolloverCompanyById]);

  useEffect(() => {
    if (activeFund) {
      setSelectedFunds({ [activeFund.id as number]: true });
    }
  }, [activeFund]);

  useEffect(() => {
    if (activeCompany) {
      setSelectedCompanies({ [activeCompany.id as number]: true });
    }
  }, [activeCompany]);

  const getSelectedFunds = useCallback(
    () =>
      items.reduce((funds, fund) => {
        let isSelected;
        if (fund.id) {
          if (fund.subItems.length === 0 || fund.subItems.every(company => !selectedCompanies?.[company.id])) {
            isSelected = selectedFunds?.[fund.id];
          } else {
            isSelected = fund.subItems.every(
              company => selectedCompanies?.[company.id] && fund.id && !funds.includes(fund.id)
            );
          }
          if (isSelected) {
            funds.push(fund.id);
          }
        }
        return funds;
      }, [] as number[]),
    [items, selectedCompanies, selectedFunds]
  );

  // we can go directly to the submit step if every company selected has no measurement dates
  const submit = useMemo(
    () =>
      selectedCompanies &&
      companies &&
      objToArray<string, boolean>(selectedCompanies)
        .filter(([, selected]) => selected)
        .map(([companyId]) => companies.find(company => company.company_id.toString() === companyId))
        .every(company => !company?.measurement_dates?.length),
    [selectedCompanies, companies]
  );

  const isStep = useCallback((step: number) => activeStep === step, [activeStep]);

  const handleBack = useCallback(() => {
    setActiveStep(lastStep => lastStep - 1);
  }, []);

  const handleCreate = useCallback(() => {
    setActiveStep(THIRD);
  }, []);

  const handleNext = useCallback(() => {
    setActiveStep(lastStep => lastStep + 1);
  }, []);

  const { launchRequests, processingRollover, result } = useRolloverCompanies({
    firmInfo,
    measurementDate,
    selectedFunds: getSelectedFunds(),
    rolloverDetailCompanies,
  });

  const disableActionButtons = useMemo(
    () =>
      !measurementDate ||
      // Every company's source date comes from the companies list, so nothing is created before it loads.
      !companiesLoaded ||
      (isEmpty(selectedFunds) && isEmpty(selectedCompanies)) ||
      processingRollover,
    [measurementDate, companiesLoaded, selectedFunds, selectedCompanies, processingRollover]
  );

  const handleSubmit = useCallback(async () => {
    launchRequests();
  }, [launchRequests]);

  const onCloseLedgerUpdate = useCallback(async () => {
    onClose();
    if (!firmInfo) {
      return;
    }
    await refreshFirmCompanies({
      firmId: firmInfo.id,
      shouldShowLoadingProgress: true,
    });
    // Firm layouts observe the refreshed company list and rebuild full CompanyInfo.
    // Avoid writing company-list rows here because they lack extended fields like measurement_date_id.
    if (getFundMeasurementDates) {
      await getFundMeasurementDates(
        result.fundResult.filter(r => r.result).map(r => r.result as MeasurementDateRolloverReturn)
      );
    }
    // Always refresh parent MD lists after create
    if (getMeasurementDates) {
      const createdMds = result.companyResult
        .map(r => r.result)
        .filter((md): md is CompanyMeasurementDate => Boolean(md));
      const preferredCreatedMD =
        (activeCompany?.id ? createdMds.find(md => md.company === activeCompany.id) : undefined) ?? createdMds[0];
      await getMeasurementDates(preferredCreatedMD);
    }
  }, [onClose, firmInfo, refreshFirmCompanies, activeCompany, getFundMeasurementDates, getMeasurementDates, result]);

  useEffect(() => {
    if (isStep(THIRD)) {
      handleSubmit();
    }
  }, [isStep, handleSubmit]);

  const getContent = () => {
    const steps = [
      <CompaniesSelector key={FIRST} activeCompany={activeCompany as CompanyInfo} />,
      <CompaniesTable key={SECOND} />,
      <RolloverStatus key={THIRD} />,
    ];

    return steps[activeStep];
  };

  const loadCompanies = useCallback(() => {
    if (firmInfo?.id) {
      getCompanies(firmInfo.id);
    }
  }, [firmInfo, getCompanies]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const requestStatus = useMemo(
    () => [...result.fundResult, ...result.companyResult, ...result.companyResult].map(({ state }) => state),
    [result]
  );

  const progress = useMemo(() => {
    const completedCount = requestStatus.filter(status => status === COMPLETE || status === FAILED).length;
    const totalCount = requestStatus.length;
    if (totalCount === 0) {
      return 0;
    }
    return Math.round((completedCount / totalCount) * 100);
  }, [requestStatus]);

  const mdContextValue = useMemo<MDContextType>(
    () => ({
      errors,
      items,
      subItems,
      selectedFundNames,
      measurementDate,
      selectedFunds,
      selectedCompanies,
      rollover,
      setMeasurementDate,
      setSelectedFunds,
      setSelectedCompanies,
      measurementDateOptionsByCompanyId: sortedMeasurementDateOptionsByCompanyId,
      rolloverDetailCompanies,
      setRolloverDetailCompanies,
      setRollover,
      setShouldResetFinancials,
      shouldResetFinancials,
      shouldExcludeDocuments,
      setShouldExcludeDocuments,
      latestPossibleMarketDate,
      marketDate,
      statusList: result,
      isLoading: loadingCompanies || processingRollover,
    }),
    [
      selectedFundNames,
      subItems,
      result,
      errors,
      items,
      loadingCompanies,
      measurementDate,
      sortedMeasurementDateOptionsByCompanyId,
      processingRollover,
      rollover,
      latestPossibleMarketDate,
      marketDate,
      selectedCompanies,
      selectedFunds,
      setShouldResetFinancials,
      shouldExcludeDocuments,
      setShouldExcludeDocuments,
      shouldResetFinancials,
      rolloverDetailCompanies,
    ]
  );

  return (
    <StyledDialog
      open={open}
      maxWidth="lg"
      title={NEW_MEASUREMENT_DATE}
      onClose={onClose}
      loadingIncrement={progress}
      isLoading={loadingCompanies || processingRollover}
      fullWidth
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown:
          !allowEscapeClose ||
          activeStep !== FIRST ||
          Boolean(measurementDate) ||
          loadingCompanies ||
          processingRollover,
        disableTitleClose: false,
      }}>
      <MDContext.Provider value={mdContextValue}>
        {!isEmpty(errors) && (
          <Collapse in={!isEmpty(errors)}>
            <Alert
              variant="outlined"
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setErrors({});
                  }}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }>
              {FIX_BEFORE_CONTINUE}
            </Alert>
          </Collapse>
        )}
        {getContent()}
        {loadingCompanies && (
          <Alert id="md-companies-loading" severity="info">
            {COMPANIES_LOADING_MESSAGE}
          </Alert>
        )}
        {companiesStatus === 'error' && (
          <Alert
            id="md-companies-error"
            severity="error"
            action={
              <Button id="md-companies-retry" color="inherit" size="small" onClick={loadCompanies}>
                Retry
              </Button>
            }>
            {COMPANIES_LOAD_FAILED_MESSAGE}
          </Alert>
        )}
        <StyledActionButtonsGrid container justifyContent="flex-end" spacing={2}>
          {isStep(FIRST) && (
            <Grid>
              <Button
                id={submit ? 'create-md-btn' : 'next-md-btn'}
                variant="contained"
                color="secondary"
                onClick={() => (submit ? handleCreate() : handleNext())}
                disabled={disableActionButtons}>
                {submit ? 'Create' : 'Next'}
              </Button>
            </Grid>
          )}
          {isStep(SECOND) && (
            <Grid>
              <Button color="primary" onClick={handleBack}>
                Back
              </Button>
            </Grid>
          )}
          {isStep(SECOND) && (
            <Grid>
              <Button
                id="rollover-ok-btn"
                color="secondary"
                onClick={handleNext}
                variant="contained"
                disabled={!companiesLoaded || processingRollover}>
                Create
              </Button>
            </Grid>
          )}
          {isStep(THIRD) && (
            <Grid>
              <Button
                id="rollover-ok-btn"
                color="secondary"
                onClick={onCloseLedgerUpdate}
                disabled={processingRollover}
                variant="contained">
                Ok
              </Button>
            </Grid>
          )}
        </StyledActionButtonsGrid>
      </MDContext.Provider>
    </StyledDialog>
  );
};

export default MeasurementDateDialog;
